export const BSB_LENGTH = 6;
export const ACCOUNT_NUMBER_MIN_LENGTH = 6;
export const ACCOUNT_NUMBER_MAX_LENGTH = 9;
export const ACCOUNT_NAME_MIN_LENGTH = 1;
export const ACCOUNT_NAME_MAX_LENGTH = 100;

const BSB_PATTERN = new RegExp(`^\\d{${BSB_LENGTH}}$`);
const ACCOUNT_NUMBER_PATTERN = new RegExp(`^\\d{${ACCOUNT_NUMBER_MIN_LENGTH},${ACCOUNT_NUMBER_MAX_LENGTH}}$`);

const INVALID_BSB_MESSAGE = 'Invalid BSB, please update.';
const INVALID_ACCOUNT_NUMBER_MESSAGE = 'Invalid account number, please update.';
const INVALID_ACCOUNT_NAME_MESSAGE = 'Invalid account name, please update.';
const GENERIC_API_ERROR_MESSAGE = 'We could not set up your direct debit. Please check your details and try again.';

/**
 * Maps the backend error codes the direct debit endpoints can return onto the
 * copy shown to the customer. Codes with a `field` are also rendered against
 * that input so the customer can see which value to correct.
 */
const API_ERRORS = {
  'invalid-bank-bsb-number-error': { field: 'bsb', message: INVALID_BSB_MESSAGE },
  'missing-bank-bsb-number-error': { field: 'bsb', message: INVALID_BSB_MESSAGE },
  'invalid-bank-account-number-error': { field: 'accountNumber', message: INVALID_ACCOUNT_NUMBER_MESSAGE },
  'missing-bank-account-number-error': { field: 'accountNumber', message: INVALID_ACCOUNT_NUMBER_MESSAGE },
  'invalid-bank-account-name-error': { field: 'bankAccountName', message: INVALID_ACCOUNT_NAME_MESSAGE },
  'missing-bank-account-name-error': { field: 'bankAccountName', message: INVALID_ACCOUNT_NAME_MESSAGE },
  'mandate-already-exists-error': {
    message: 'A direct debit is already set up for this loan. Please refresh the page and try again.',
  },
  'direct-debit-setup-failed-error': {
    message: 'We could not set up your direct debit with these details. Please check them and try again.',
  },
  'loan-not-in-valid-stage-error': {
    message: 'Your loan is not at a stage where bank account details can be updated.',
  },
};

/**
 * Strips non-digits and caps the length so the value can never exceed what the
 * API accepts.
 *
 * @param {string} value
 * @param {number} maxLength
 * @returns {string}
 */
export function sanitizeDigits(value, maxLength) {
  return (value || '').replace(/\D/g, '').slice(0, maxLength);
}

/**
 * Caps the length so the value can never exceed what the API accepts.
 *
 * @param {string} value
 * @param {number} maxLength
 * @returns {string}
 */
export function sanitizeText(value, maxLength) {
  return (value || '').slice(0, maxLength);
}

/**
 * @typedef {Object} BankDetails
 * @property {string} bankAccountName
 * @property {string} financialInstitutionName
 * @property {string} bsb
 * @property {string} accountNumber
 * @property {boolean} directDebitAuthority
 * @property {boolean} accountHolderAuthority
 */

/**
 * @typedef {Object} BankDetailsErrors
 * @property {string} [bankAccountName]
 * @property {string} [financialInstitutionName]
 * @property {string} [bsb]
 * @property {string} [accountNumber]
 * @property {string} [directDebitAuthority]
 * @property {string} [accountHolderAuthority]
 */

/**
 * Validates the direct debit bank account fields.
 *
 * Returns an errors object keyed by field name. An empty object means all
 * fields are valid.
 *
 * @param {BankDetails} details
 * @returns {BankDetailsErrors}
 */
export function validateBankDetails(details) {
  const errors = {};
  const bankAccountName = (details.bankAccountName || '').trim();
  if (!bankAccountName) {
    errors.bankAccountName = 'Account name is required';
  } else if (bankAccountName.length < ACCOUNT_NAME_MIN_LENGTH || bankAccountName.length > ACCOUNT_NAME_MAX_LENGTH) {
    errors.bankAccountName = `Account name must be between ${ACCOUNT_NAME_MIN_LENGTH} and ${ACCOUNT_NAME_MAX_LENGTH} characters`;
  }
  if (!(details.financialInstitutionName || '').trim()) {
    errors.financialInstitutionName = 'Name of financial institution is required';
  }
  if (!BSB_PATTERN.test(details.bsb || '')) errors.bsb = INVALID_BSB_MESSAGE;
  if (!ACCOUNT_NUMBER_PATTERN.test(details.accountNumber || '')) {
    errors.accountNumber = INVALID_ACCOUNT_NUMBER_MESSAGE;
  }
  if (!details.directDebitAuthority) errors.directDebitAuthority = 'Direct debit authority is required';
  if (!details.accountHolderAuthority) {
    errors.accountHolderAuthority = 'Account holder or signatory confirmation is required';
  }
  return errors;
}

/**
 * Translates a failed direct debit request into customer-facing copy. The API
 * replies with machine codes such as `invalid-bank-bsb-number-error`, which must
 * never reach the screen; anything unrecognised (including a 500 or a dropped
 * connection) falls back to a generic message.
 *
 * @param {{ response?: { data?: { message?: string | string[] } } }} error The rejected request error
 * @returns {{ fieldErrors: Record<string, string>, message: string }}
 */
export function mapBankDetailsApiError(error) {
  const responseMessage = error?.response?.data?.message;
  const codes = Array.isArray(responseMessage) ? responseMessage : [responseMessage];
  const fieldErrors = {};
  const messages = [];
  codes.forEach((code) => {
    const mapped = API_ERRORS[code];
    if (!mapped) return;
    if (mapped.field) fieldErrors[mapped.field] = mapped.message;
    messages.push(mapped.message);
  });
  return { fieldErrors, message: messages[0] || GENERIC_API_ERROR_MESSAGE };
}
